import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import { usePaginatedQuery } from "./usePaginatedQuery";
import {
  parseSearchParams,
  serializeToSearchParams,
  cleanEmptyStrings,
} from "./utils/searchParams.utils";
import type { PaginatedQueryParams, PaginatedResult } from "@shared/api/types/pagination.types";
import { AxiosError } from "axios";

interface UseUrlPaginatedQueryOptions<TData, TFilters> {
  endpoint: string;
  queryKeyBase: string[];
  filterKeys: (keyof TFilters & string)[];
  defaultLimit?: number;
  enabled?: boolean;
  onSuccess?: (data: PaginatedResult<TData>) => void;
  onError?: (error: AxiosError) => void;
}

export const useUrlPaginatedQuery = <TData, TFilters = Record<string, never>>({
  endpoint,
  queryKeyBase,
  filterKeys,
  defaultLimit,
  enabled = true,
  onSuccess,
  onError,
}: UseUrlPaginatedQueryOptions<TData, TFilters>) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const currentFilters = useMemo(
    () => parseSearchParams<TFilters>(searchParams, filterKeys),
    [searchParams, filterKeys],
  );

  const page = searchParams.get("page")
    ? Number(searchParams.get("page"))
    : undefined;
  const limit = searchParams.get("limit")
    ? Number(searchParams.get("limit"))
    : defaultLimit;

  const params = useMemo<PaginatedQueryParams<TFilters>>(
    () =>
      ({ page, limit, ...currentFilters }) as PaginatedQueryParams<TFilters>,
    [page, limit, currentFilters],
  );

  const query = usePaginatedQuery<TData, TFilters>({
    endpoint,
    queryKeyBase,
    params,
    enabled,
    onSuccess,
    onError,
  });

  const setFilters = useCallback(
    (filters: Partial<TFilters>) => {
      const cleaned = cleanEmptyStrings(filters as Record<string, unknown>);
      setSearchParams((prev) =>
        serializeToSearchParams({ ...cleaned, page: 1 }, prev),
      );
    },
    [setSearchParams],
  );

  const setPage = useCallback(
    (newPage: number) => {
      setSearchParams((prev) =>
        serializeToSearchParams({ page: newPage }, prev),
      );
    },
    [setSearchParams],
  );

  const setLimit = useCallback(
    (newLimit: number) => {
      setSearchParams((prev) =>
        serializeToSearchParams({ page: 1, limit: newLimit }, prev),
      );
    },
    [setSearchParams],
  );

  const resetFilters = useCallback(() => {
    setSearchParams((prev) => {
      const reset: Record<string, unknown> = { page: undefined };
      for (const key of filterKeys) reset[key] = undefined;
      return serializeToSearchParams(reset, prev);
    });
  }, [setSearchParams, filterKeys]);

  return {
    query,

    data: query.data,
    isLoading: query.isLoadingFirstTime,
    isRefreshing: query.isRefreshing,
    isError: query.isError,
    error: query.error,
    isEmpty: query.isEmpty,
    totalPages: query.totalPages,
    totalItems: query.totalItems,
    setFilters,
    setPage,
    setLimit,
    resetFilters,
    currentFilters,
  };
};
